"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useNotifyInvalidSlot = exports.useAvailableSlots = exports.getPageSizeFromProps = exports.getDateRangeFromProps = void 0;
var _lodash = require("lodash");
var _react = require("react");
var _momentTimezone = _interopRequireDefault(require("moment-timezone"));
var _utils = require("../../utils");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
var DEFAULT_PAGE_SIZE = 4;
var AVAILABILITY_REQUEST_RANGE_DAYS = 180;
// Members may only book appointments at least 24 hours in advance. Add a 1-hour buffer to
// minimize odds of showing them an appointment that's no longer bookable upon submission.
var AVAILABILITY_REQUEST_BUFFER_HOURS = 25;
var getDateRangeFromProps = function getDateRangeFromProps(user) {
  var start = (0, _utils.isAdmin)(user) ?
  // Add 1 minute to ensure start timestamp is in the future when sent to Cronofy
  // By the time moment() is invoked and passed to Cronofy API
  // the timestamp will be a few milliseconds in the past
  (0, _momentTimezone.default)().add(1, 'minute').startOf('minute') : (0, _momentTimezone.default)().add(AVAILABILITY_REQUEST_BUFFER_HOURS, 'hours').startOf('minute');
  var end = (0, _momentTimezone.default)(start).add(AVAILABILITY_REQUEST_RANGE_DAYS, 'days').startOf('day');
  return {
    start: start,
    end: end
  };
};
exports.getDateRangeFromProps = getDateRangeFromProps;
var getPageSizeFromProps = function getPageSizeFromProps(props) {
  return props.pageSize || DEFAULT_PAGE_SIZE;
};
exports.getPageSizeFromProps = getPageSizeFromProps;
var compareSlots = function compareSlots(slot1, slot2) {
  return (0, _momentTimezone.default)(slot1.start).valueOf() === (0, _momentTimezone.default)(slot2.start).valueOf() && (0, _momentTimezone.default)(slot1.end).valueOf() === (0, _momentTimezone.default)(slot2.end).valueOf();
};

// Find the slot with the nearest start to the desired time
var findNearestSlot = function findNearestSlot(slots, time) {
  return slots.reduce(function (nearestSlot, currentSlot) {
    var currentDiff = currentSlot.start.diff(time);
    var previousDiff = nearestSlot.start.diff(time);
    if (Math.abs(currentDiff) < Math.abs(previousDiff)) {
      return currentSlot;
    }
    return nearestSlot;
  }, slots[0]);
};

// In the patient app, if a member goes to a URL with a "time" query string parameter,
// it is possible for the initial value of SingleAppointment selector to not exist in slots
// Check if the specified time is in the available slots loaded by SingleAppointmentSelector
var useNotifyInvalidSlot = function useNotifyInvalidSlot(_ref) {
  var ready = _ref.ready,
    slots = _ref.slots,
    value = _ref.value,
    onSelect = _ref.onSelect;
  var slotIsAvailable = value ? slots.find(function (slot) {
    return compareSlots(slot, value);
  }) : true;
  (0, _react.useEffect)(function () {
    if (!ready || !value) {
      return;
    }
    var providedTime = (0, _momentTimezone.default)(value.start);
    var nearestSlot = findNearestSlot(slots, providedTime);
    if (nearestSlot.start.diff(providedTime) !== 0) {
      _utils.toast.info({
        title: 'Time not available',
        description: "The time slot you\u2019re looking for is not available. The closest available time is ".concat(nearestSlot.start.format('dddd, MMMM D [at] h:mma, z'), "."),
        duration: 10000
      });
      onSelect(nearestSlot);
    }
    // We only want to invoke this effect in the situation where:
    // Patient goes to URL -> AppointmentCubby is mounted -> AppointmentCubby calculates
    // initial slot value from URL -> SingleAppointmentSelector is mounted with onSelect and value defined
    // -> SingleAppointmentSelect fetches slots from Cronofy
    // so we only need to check that ready has changed (to true)
    // and also don't need to worry about stale closure for value or slots
  }, [ready]);
  return {
    pending: slotIsAvailable ? false : !ready
  };
};
exports.useNotifyInvalidSlot = useNotifyInvalidSlot;
var TIMESLOT_START_DIFF = 15; // minutes

var buildTimeslotsForRange = function buildTimeslotsForRange(_ref2, duration) {
  var start = _ref2.start,
    end = _ref2.end;
  var roundTo15Mins = Math.ceil(start.minute() / TIMESLOT_START_DIFF) * TIMESLOT_START_DIFF;
  var startRounded = (0, _momentTimezone.default)(start).set('minutes', roundTo15Mins);
  if ((0, _momentTimezone.default)(end).diff(startRounded, 'minutes') < duration) {
    return [];
  }
  var slots = [];
  slots.push({
    start: startRounded,
    end: (0, _momentTimezone.default)(startRounded).add(duration, 'minutes')
  });
  while ((0, _momentTimezone.default)(end).diff(slots[slots.length - 1].end, 'minutes') > TIMESLOT_START_DIFF) {
    var lastSlot = (0, _lodash.last)(slots);
    var newSlotStart = (0, _momentTimezone.default)(lastSlot.start).add(TIMESLOT_START_DIFF, 'minutes');
    slots.push({
      start: newSlotStart,
      end: (0, _momentTimezone.default)(newSlotStart).add(duration, 'minutes')
    });
  }
  return slots;
};
var mapAvailableSlotsToBuiltSlots = function mapAvailableSlotsToBuiltSlots() {
  var managedAvailabilitySlots = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var builtSlots = arguments.length > 1 ? arguments[1] : undefined;
  var availableSlotsByStartTime = (0, _lodash.keyBy)(managedAvailabilitySlots, function (slot) {
    return slot.start.format();
  });
  return builtSlots.map(function (slot) {
    return _objectSpread(_objectSpread({}, slot), {}, {
      isFromManagedAvailability: !!availableSlotsByStartTime[slot.start.format()]
    });
  });
};
var useAvailableSlots = function useAvailableSlots(_ref3) {
  var clinicians = _ref3.clinicians,
    start = _ref3.start,
    end = _ref3.end,
    duration = _ref3.duration,
    showOnlyManagedAvailability = _ref3.showOnlyManagedAvailability,
    _ref3$onError = _ref3.onError,
    onError = _ref3$onError === void 0 ? _lodash.noop : _ref3$onError;
  // Cronofy availability request will only return time slots in managed availability
  // so if we need to show other time slots, we need to generate them ourselves
  var builtTimeslots = (0, _react.useMemo)(function () {
    return showOnlyManagedAvailability ? [] : buildTimeslotsForRange({
      start: start,
      end: end
    }, duration);
  }, [showOnlyManagedAvailability]);
  var _useRequest = (0, _utils.useRequest)((0, _utils.getClinicianAvailabilityUrl)({
      subs: clinicians.map(function (clinician) {
        return clinician.cronofySub;
      }),
      start: start,
      end: end,
      duration: duration
    }), {
      formatResult: function formatResult(data) {
        var formattedData = data.map(function (slot) {
          return (0, _utils.mapSlotToMoments)(slot, {
            isFromManagedAvailability: true
          });
        });
        var newSlots = showOnlyManagedAvailability ? formattedData : mapAvailableSlotsToBuiltSlots(formattedData, builtTimeslots);
        return newSlots;
      },
      onError: onError
    }),
    _useRequest$data = _useRequest.data,
    slots = _useRequest$data === void 0 ? [] : _useRequest$data,
    loading = _useRequest.loading;
  return {
    slots: slots,
    loading: loading
  };
};
exports.useAvailableSlots = useAvailableSlots;